import { ReactNode, useMemo } from 'react';
import { Radio } from 'antd';
import { useNavigate, NavigateOptions, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { suppressHref } from '../useHrefWithQueryParams';
import { radioButtonPaddingInline } from '@atlas-frontend/design-tokens';

export interface ButtonGroupNavItem<K extends string> {
  key: K;
  label: ReactNode;
  to: string;
  navigateOptions?: NavigateOptions;
  disabled?: boolean;
}

export interface ButtonGroupNavProps<K extends string = string> {
  items: ButtonGroupNavItem<K>[];
  currentKey?: K;
}

export const ButtonGroupNav = (props: ButtonGroupNavProps) => {
  const { items } = props;
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const currentKey = useMemo(() => {
    if (props.currentKey) {
      return props.currentKey;
    }

    let current: ButtonGroupNavItem<string> | undefined;

    for (const item of items) {
      const [toPath] = item.to.split('?');

      if (pathname + search === item.to) {
        current = item;
        break;
      } else if (pathname === toPath) {
        current = item;
      }
    }

    return current?.key;
  }, [items, pathname, search, props.currentKey]);

  return (
    <StyledRadioGroup value={currentKey}>
      {items.map(({ key, label, to, navigateOptions, disabled }) => (
        <StyledRadioButton key={key} value={key} disabled={disabled}>
          {disabled ? (
            <DisabledContent>{label}</DisabledContent>
          ) : (
            <StyledAnchor
              id={`button-group-nav-${key}`}
              href={to}
              onClick={suppressHref(() => {
                const [toPath, toSearch] = to.split('?');

                if (pathname !== toPath || search !== `?${toSearch}`) {
                  navigate(to, navigateOptions);
                }
              })}
            >
              {label}
            </StyledAnchor>
          )}
        </StyledRadioButton>
      ))}
    </StyledRadioGroup>
  );
};

const StyledRadioGroup = styled(Radio.Group)`
  white-space: nowrap;
`;

const StyledRadioButton = styled(Radio.Button)`
  padding: 0 !important;
  span {
    width: 100%;
    display: block;
  }
`;

const RadioButtonContent = css`
  width: 100%;
  display: block;
  padding-left: ${radioButtonPaddingInline}px;
  padding-right: ${radioButtonPaddingInline}px;
`;

const DisabledContent = styled.span`
  ${RadioButtonContent}
`;

const StyledAnchor = styled.a`
  color: inherit !important;
  ${RadioButtonContent}
`;
